import {
  AbstractMesh,
  ArcRotateCamera,
  Color3,
  Color4,
  Engine,
  HemisphericLight,
  Matrix,
  Mesh,
  Scene,
  Vector3,
} from '@babylonjs/core'
import { IfcLoader } from './ifcLoader'
import { computeBoundingBox } from './utils'
import { BimObject } from '../models/Bim'
import { isDevelop } from '../utils/Common'

const MAX_CACHE_SIZE = 20

export class BimManager {
  private static instance: BimManager | null = null
  private canvas?: HTMLCanvasElement
  private engine?: Engine
  private scene?: Scene
  private camera?: ArcRotateCamera
  private light?: HemisphericLight
  private resizeListener?: (this: Window, ev: UIEvent) => any

  private bimObjectsCache = new Map<string, AbstractMesh>()
  private currentLoadToken: number = 0
  private currentVisibleMesh?: AbstractMesh

  static getInstance(): BimManager {
    if (!BimManager.instance) {
      BimManager.instance = new BimManager()
    }
    return BimManager.instance
  }

  getScene() {
    return this.scene
  }

  async initialize(canvas: HTMLCanvasElement) {
    if (!this.engine) {
      this.canvas = canvas
      this.engine = new Engine(canvas, true, {
        antialias: true,
        stencil: true,
      })

      this.scene = new Scene(this.engine)
      this.scene.clearColor = Color4.FromHexString('#F4F9FF')
      this.scene.useRightHandedSystem = true

      this.camera = new ArcRotateCamera(
        'Camera',
        -Math.PI / 2,
        Math.PI / 3,
        5,
        new Vector3(0, 0, 0),
        this.scene,
      )
      this.camera.attachControl(this.canvas, true)
      this.camera.panningSensibility = 0
      this.camera.inputs.removeByType('ArcRotateCameraMouseWheelInput')

      this.light = new HemisphericLight(
        'hemiLight',
        new Vector3(1, 0, 1),
        this.scene,
      )
      this.light.groundColor = Color3.White()
      this.light.intensity = 0.87

      this.resizeListener = () => {
        if (!this.engine) {
          return
        }

        this.engine.resize()
      }

      window.addEventListener('resize', this.resizeListener)
      this.startRenderLoop()
    }
  }

  startRenderLoop(): void {
    this.engine!.runRenderLoop(() => {
      this.scene!.render()
    })
  }

  stopRenderLoop(): void {
    if (this.engine) {
      this.engine!.stopRenderLoop()
    }
  }

  zoomCamera(ratio: number) {
    if (this.camera) {
      this.camera.radius -= (this.camera.radius / 5) * ratio
    }
  }

  async showBimObject(object?: BimObject | null) {
    const loadToken = ++this.currentLoadToken

    // Disable the last mesh in cache (most recently used)
    const keys = Array.from(this.bimObjectsCache.keys())
    if (keys.length > 0) {
      const lastKey = keys[keys.length - 1]
      const lastMesh = this.bimObjectsCache.get(lastKey)
      if (lastMesh) {
        lastMesh.setEnabled(false)
      }
    }

    if (this.currentVisibleMesh) {
      this.currentVisibleMesh.setEnabled(false)
      this.currentVisibleMesh = undefined
    }

    if (!!object) {
      await this.loadBimMesh(object).then((mesh) => {
        if (mesh && loadToken === this.currentLoadToken) {
          mesh.setEnabled(true)
          this.currentVisibleMesh = mesh
          this.centerCamera()
        }
      })
    }
  }

  dispose() {
    window.removeEventListener('resize', this.resizeListener!)
    this.stopRenderLoop()
    this.scene?.dispose()
    this.engine?.dispose()
    this.engine = undefined
    this.bimObjectsCache.clear()
    BimManager.instance = null
  }

  private async loadBimMesh(object: BimObject) {
    if (this.bimObjectsCache.has(object.id)) {
      // Move to end to mark as recently used
      const mesh = this.bimObjectsCache.get(object.id)!
      this.bimObjectsCache.delete(object.id)
      this.bimObjectsCache.set(object.id, mesh)
      return mesh
    }

    try {
      const response = isDevelop()
        ? await fetch(object.binaryUrl)
        : await fetch(object.binaryUrl, {
            headers: {
              'Access-Control-Allow-Origin': '*',
            },
          })
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`)
      }

      const arrayBuffer = await response.arrayBuffer()
      const uint8Array = new Uint8Array(arrayBuffer)

      const mesh = await IfcLoader.getInstance().load(
        uint8Array,
        this.scene!,
        true,
      )
      mesh.id = object.id
      mesh.name = object.name

      const scaling = new Vector3(1, 1, 1)
      const bbox = computeBoundingBox(mesh.getChildMeshes(), true)
      if (object.length && object.height && object.thickness) {
        scaling.x = object.length / (bbox.max.x - bbox.min.x)
        scaling.y = object.height / (bbox.max.y - bbox.min.y)
        scaling.z = object.thickness / (bbox.max.z - bbox.min.z)
      }

      const bboxCenter = Vector3.TransformCoordinates(
        Vector3.Center(bbox.min, bbox.max),
        Matrix.Scaling(scaling.x, scaling.y, scaling.z),
      )
      mesh.scaling.set(scaling.x, scaling.y, scaling.z)
      mesh.position.subtractInPlace(bboxCenter)
      mesh.computeWorldMatrix(true)
      mesh
        .getChildMeshes()
        .forEach((m) => (m as Mesh).bakeCurrentTransformIntoVertices())
      mesh.position.set(0, 0, 0)
      mesh.scaling.set(1, 1, 1)

      mesh.setEnabled(false)
      this.bimObjectsCache.set(object.id, mesh)

      // Enforce cache size limit
      if (this.bimObjectsCache.size > MAX_CACHE_SIZE) {
        const oldestKey = this.bimObjectsCache.keys().next().value
        if (oldestKey) {
          const oldestMesh = this.bimObjectsCache.get(oldestKey)
          if (oldestMesh) {
            oldestMesh.dispose()
          }
          this.bimObjectsCache.delete(oldestKey)
        }
      }

      return mesh
    } catch (error) {
      console.error('Error loading IFC file:', error)
      return null
    }
  }

  private centerCamera() {
    const meshes = this.scene?.meshes.filter(
      (mesh) =>
        mesh.isVisible && mesh.getTotalVertices() > 0 && mesh.isEnabled(),
    )

    if (!this.camera || !meshes) {
      return
    }

    const { min, max } = computeBoundingBox(meshes)
    const center = Vector3.Center(min, max)
    const sizeVec = max.subtract(min)
    const size = sizeVec.length()

    this.camera.setTarget(center)
    this.camera.radius = size * 2.5
    this.camera.upperRadiusLimit = this.camera.radius * 2
    this.camera.lowerRadiusLimit = this.camera.radius / 2
  }
}
